import React, { useMemo } from 'react';
import Button from '@atlaskit/button';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { Attributes } from '../../common/types/index.tsx';
import { saveFilterDialogEntryPoint } from './entrypoint.tsx';

export type Props = {
	attributes?: Attributes;
	jql: string;
	filterId?: string;
	buttonText: string;
	title: string;
	onFilterSave?: (filterId: string) => void;
};

export const SaveFilterDialog = ({ buttonText, filterId, ...props }: Props): React.JSX.Element => {
	const modalProps = useMemo(
		() => ({
			width: 'medium',
			shouldScrollInViewport: true,
		}),
		[],
	);

	const entryPointProps = useMemo(
		() => ({
			filterId,
			...props,
		}),
		[filterId, props],
	);

	const cloudId = useCloudId();
	const activationId = useActivationId();
	const entryPointParams = useMemo(
		() => ({
			filterAri:
				filterId !== null && filterId !== undefined
					? createAri({
							resourceOwner: 'jira',
							cloudId,
							resourceType: 'filter',
							resourceId: filterId,
							activationId,
						})
					: undefined,
		}),
		[filterId, cloudId, activationId],
	);

	return (
		<ModalEntryPointPressableTrigger
			modalProps={modalProps}
			entryPoint={saveFilterDialogEntryPoint}
			entryPointProps={entryPointProps}
			entryPointParams={entryPointParams}
			interactionName="jiraSaveFilterDialog.SaveFilter"
			useInternalModal
		>
			{({ ref }) => (
				<Button
					onClick={(_, analyticsEvent) => fireUIAnalytics(analyticsEvent, 'saveFilterDialog')}
					appearance="link"
					testId="save-filter-dialog.ui.save-filter-dialog.button"
					ref={ref}
				>
					{buttonText}
				</Button>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

export default SaveFilterDialog;
